import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Timer, AlertTriangle, Users, Target, Shield, 
  Zap, Hand, Clock, Flag, Clipboard 
} from 'lucide-react';
import { GameRosterPlayer } from '@/services/gameRosterService';

interface SportEventControlsProps {
  sportName: string;
  onEvent: (event: any) => void;
  game?: any;
  gameRoster?: GameRosterPlayer[];
}

const SPORT_EVENTS = {
  'Basketball': [
    { type: 'foul', label: 'Foul', icon: Hand, variant: 'destructive' as const },
    { type: 'timeout', label: 'Timeout', icon: Clock, variant: 'secondary' as const },
    { type: 'turnover', label: 'Turnover', icon: AlertTriangle, variant: 'outline' as const },
    { type: 'rebound', label: 'Rebound', icon: Target, variant: 'default' as const },
    { type: 'assist', label: 'Assist', icon: Users, variant: 'default' as const },
    { type: 'substitution', label: 'Substitution', icon: Users, variant: 'secondary' as const },
  ],
  'Football': [
    { type: 'incomplete_pass', label: 'Incomplete Pass', icon: Target, variant: 'outline' as const },
    { type: 'penalty', label: 'Penalty', icon: Flag, variant: 'destructive' as const },
    { type: 'timeout', label: 'Timeout', icon: Clock, variant: 'secondary' as const },
    { type: 'out_of_bounds', label: 'Out of Bounds', icon: AlertTriangle, variant: 'outline' as const },
    { type: 'injury', label: 'Injury Timeout', icon: Shield, variant: 'destructive' as const },
    { type: 'first_down', label: 'First Down', icon: Target, variant: 'default' as const },
  ],
  'Soccer': [
    { type: 'foul', label: 'Foul', icon: Hand, variant: 'destructive' as const },
    { type: 'yellow_card', label: 'Yellow Card', icon: Clipboard, variant: 'secondary' as const },
    { type: 'red_card', label: 'Red Card', icon: Clipboard, variant: 'destructive' as const },
    { type: 'corner_kick', label: 'Corner Kick', icon: Flag, variant: 'outline' as const },
    { type: 'substitution', label: 'Substitution', icon: Users, variant: 'secondary' as const },
    { type: 'offside', label: 'Offside', icon: AlertTriangle, variant: 'outline' as const },
  ],
  'Baseball': [
    { type: 'strike', label: 'Strike', icon: Target, variant: 'destructive' as const },
    { type: 'ball', label: 'Ball', icon: Target, variant: 'outline' as const },
    { type: 'hit', label: 'Hit', icon: Zap, variant: 'default' as const },
    { type: 'out', label: 'Out', icon: AlertTriangle, variant: 'destructive' as const },
    { type: 'walk', label: 'Walk', icon: Users, variant: 'secondary' as const },
    { type: 'steal', label: 'Stolen Base', icon: Timer, variant: 'default' as const },
  ]
};

const SportEventControls = ({ sportName, onEvent, game, gameRoster = [] }: SportEventControlsProps) => {
  const events = SPORT_EVENTS[sportName as keyof typeof SPORT_EVENTS] || SPORT_EVENTS['Basketball'];
  const [selectedEvent, setSelectedEvent] = useState<{type: string, label: string} | null>(null);
  
  // Get players from game roster
  const homePlayers = gameRoster.filter(p => p.team_side === 'home' && p.is_present);
  const awayPlayers = gameRoster.filter(p => p.team_side === 'away' && p.is_present);

  const eventsNeedingPlayer = ['foul', 'penalty', 'yellow_card', 'red_card', 'substitution'];

  const handleEventClick = (eventType: string, label: string, playerId?: string, playerName?: string, team?: string) => {
    const event = {
      type: eventType,
      label: playerId ? `${label} - ${playerName} (${team})` : label,
      timestamp: new Date().toISOString(),
      sport: sportName,
      id: Date.now(),
      playerId,
      playerName,
      team
    };
    
    console.log('Game event triggered:', event);
    onEvent(event);
    
    // Enhanced visual feedback
    const button = document.activeElement as HTMLButtonElement;
    if (button) {
      button.style.transform = 'scale(0.95)';
      button.style.backgroundColor = 'hsl(var(--primary))';
      button.style.color = 'hsl(var(--primary-foreground))';
      
      setTimeout(() => {
        button.style.transform = 'scale(1)';
        button.style.backgroundColor = '';
        button.style.color = '';
      }, 300);
    }
    
    // Show brief success message
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md z-50 transition-opacity';
    toast.textContent = `${label} logged`;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 1500);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Timer className="h-5 w-5" />
          Game Events
          <Badge variant="outline">{sportName}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {events.map((event) => {
            const IconComponent = event.icon;
            const needsPlayer = eventsNeedingPlayer.includes(event.type);
            
            if (needsPlayer) {
              return (
                <Dialog key={event.type}>
                  <DialogTrigger asChild>
                    <Button
                      variant={event.variant}
                      onClick={() => setSelectedEvent(event)}
                      className="h-16 flex flex-col gap-1"
                    >
                      <IconComponent className="h-5 w-5" />
                      <span className="text-xs">{event.label}</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Select Player for {event.label}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-medium mb-2 text-foreground">
                          {game?.home_team_name || game?.homeTeamName || 'Home Team'}
                        </h4>
                        <div className="space-y-2">
                          {homePlayers.length > 0 ? homePlayers.map((player) => (
                            <Button
                              key={player.id}
                              variant="outline"
                              onClick={() => {
                                handleEventClick(event.type, event.label, player.id, player.player_name, 'home');
                                setSelectedEvent(null);
                              }}
                              className="w-full justify-start"
                            >
                              {player.player_name} {player.position && `(${player.position})`}
                            </Button>
                          )) : (
                            <div className="text-sm text-muted-foreground">No players present</div>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2 text-foreground">
                          {game?.away_team_name || game?.awayTeamName || 'Away Team'}
                        </h4>
                        <div className="space-y-2">
                          {awayPlayers.length > 0 ? awayPlayers.map((player) => (
                            <Button
                              key={player.id}
                              variant="outline"
                              onClick={() => {
                                handleEventClick(event.type, event.label, player.id, player.player_name, 'away');
                                setSelectedEvent(null);
                              }}
                              className="w-full justify-start"
                            >
                              {player.player_name} {player.position && `(${player.position})`}
                            </Button>
                          )) : (
                            <div className="text-sm text-muted-foreground">No players present</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              );
            }
            
            return (
              <Button
                key={event.type}
                variant={event.variant}
                onClick={() => handleEventClick(event.type, event.label)}
                className="h-16 flex flex-col gap-1"
              >
                <IconComponent className="h-5 w-5" />
                <span className="text-xs">{event.label}</span>
              </Button>
            );
          })}
        </div>

        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="font-medium text-foreground mb-2">Quick Instructions:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Click events as they happen during the game</li>
            <li>• Events will automatically stop the clock if applicable</li>
            <li>• All events are logged with timestamps</li>
            <li>• Use the Play by Play tab to review event history</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default SportEventControls;